import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { trackConversion } from "@/lib/gads";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Home as HomeIcon, TrendingUp, Sparkles, Heart, Calendar, Coins, ChevronRight, Camera, ArrowLeft, FileText, Upload, Trash2, Bell, BellOff, StickyNote, Clock, X } from "lucide-react";
import FunnelWidget from "@/components/FunnelWidget";
import { API } from "@/lib/api";
import axios from "axios";

const DOC_KINDS = [
  { v: "energielabel", label: "Energielabel" },
  { v: "eigendomsbewijs", label: "Eigendomsbewijs" },
  { v: "tekening", label: "Plattegrond / tekening" },
  { v: "vve", label: "VvE-stukken" },
  { v: "keuring", label: "Keuringsrapport" },
  { v: "overig", label: "Overig" },
];

const STATUS_LABEL = {
  submitted: "Ingediend — wacht op beoordeling", awaiting_review: "In behandeling",
  review_in_progress: "Beoordeling gaande", contact_seller: "Wij nemen contact op",
  appointment_scheduled: "Afspraak gepland", photos_received: "Foto's ontvangen",
  approved: "Goedgekeurd", published: "Gepubliceerd (live)", paused: "Gepauzeerd",
  offline: "Offline", sold: "Verkocht", rejected: "Afgewezen",
};
const INT_STATUS = { new: "Nieuw", contacted: "Contact gehad", viewing_scheduled: "Bezichtiging gepland", not_interested: "Geen interesse meer", converted_to_offer: "Bod uitgebracht", closed: "Afgesloten" };
const VW_STATUS = { requested: "Aangevraagd", confirmed: "Bevestigd", rescheduled: "Verzet", completed: "Uitgevoerd", no_show: "Niet verschenen", cancelled: "Geannuleerd" };
const OF_STATUS = { submitted: "Ingediend", communicated_to_seller: "Doorgegeven", counter_offer: "Tegenbod", accepted: "Geaccepteerd", rejected: "Afgewezen", withdrawn: "Ingetrokken", sold: "Verkocht" };
const eur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function SellerDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("submitted") === "1") {
      trackConversion();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState({ matches: null, interests: null, viewings: null, offers: null });
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [notif, setNotif] = useState({ enabled: false, min_score: 80 });
  const [noteText, setNoteText] = useState("");
  const [slotForm, setSlotForm] = useState({ date: "", start_time: "10:00", end_time: "11:00", location_note: "", max_bookings: 1 });
  const [uploadKind, setUploadKind] = useState("energielabel");
  const [uploadVisible, setUploadVisible] = useState(true);

  const reloadDocs = async (pid) => {
    try { const r = await http.get(`/seller/properties/${pid}/documents`); setDocs(r.data); }
    catch (e) { console.error("reloadDocs failed:", e); }
  };
  const reloadNotes = async (pid) => {
    try { const r = await http.get(`/seller/properties/${pid}/notes`); setNotes(r.data); }
    catch (e) { console.error("reloadNotes failed:", e); }
  };
  const reloadSlots = async (pid) => {
    try { const r = await http.get(`/seller/properties/${pid}/slots`); setSlots(r.data); }
    catch (e) { console.error("reloadSlots failed:", e); }
  };

  useEffect(() => {
    http.get("/seller/properties").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openProperty = async (p) => {
    setSelected(p); setTab("overview"); setDetail({ matches: null, interests: null, viewings: null, offers: null });
    setNotif({ enabled: !!p.match_notifications_enabled, min_score: p.match_notifications_min_score || 80 });
    setBusy(true);
    try {
      const [m, i, v, o] = await Promise.all([
        http.get(`/seller/properties/${p.id}/matches`),
        http.get(`/seller/properties/${p.id}/interests`),
        http.get(`/seller/properties/${p.id}/viewings`),
        http.get(`/seller/properties/${p.id}/offers`),
      ]);
      setDetail({ matches: m.data.results, interests: i.data, viewings: v.data, offers: o.data });
      await Promise.all([reloadDocs(p.id), reloadNotes(p.id), reloadSlots(p.id)]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadDoc = async (file) => {
    if (!file || !selected) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", uploadKind);
    fd.append("visible_to_buyers", uploadVisible ? "true" : "false");
    try {
      const token = localStorage.getItem("wv_token");
      await axios.post(`${API}/seller/properties/${selected.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: true,
      });
      toast.success("Document geüpload");
      await reloadDocs(selected.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteDoc = async (id) => {
    if (!window.confirm("Document verwijderen?")) return;
    try { await http.delete(`/seller/documents/${id}`); await reloadDocs(selected.id); toast.success("Verwijderd"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    try { await http.post(`/seller/properties/${selected.id}/notes`, { text: noteText.trim() }); setNoteText(""); await reloadNotes(selected.id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteNote = async (id) => {
    try { await http.delete(`/seller/notes/${id}`); await reloadNotes(selected.id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveNotif = async (next) => {
    setNotif(next);
    try { await http.patch(`/seller/properties/${selected.id}/notifications`, { match_notifications_enabled: next.enabled, min_score: next.min_score }); toast.success("Notificatie-instellingen opgeslagen"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const addSlot = async () => {
    if (!slotForm.date || !slotForm.start_time || !slotForm.end_time) return toast.error("Vul datum en tijden in");
    try {
      await http.post(`/seller/properties/${selected.id}/slots`, { ...slotForm, max_bookings: parseInt(slotForm.max_bookings) || 1 });
      setSlotForm({ date: "", start_time: "10:00", end_time: "11:00", location_note: "", max_bookings: 1 });
      await reloadSlots(selected.id);
      toast.success("Slot toegevoegd");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteSlot = async (id) => {
    if (!window.confirm("Slot verwijderen?")) return;
    try { await http.delete(`/seller/slots/${id}`); await reloadSlots(selected.id); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (selected) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="seller-back"><ArrowLeft className="w-4 h-4" /> Terug naar overzicht</button>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase font-bold tracking-wider text-slate-500">{selected.property_type}</p>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold">{selected.title}</h1>
            <p className="mt-1 text-sm text-slate-600">{selected.address}, {selected.city} · <span className="font-mono-num font-bold">{eur(selected.price)}</span></p>
          </div>
          <p className="wv-status-pill wv-status-review text-sm">{STATUS_LABEL[selected.status] || selected.status}</p>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1"><FunnelWidget propertyId={selected.id} scope="seller" /></div>
          <div className="md:col-span-2 p-6 rounded-2xl bg-white border border-slate-200">
            <p className="font-display font-bold text-lg">Wilt u een bezichtiging via Woon Verder?</p>
            <p className="mt-2 text-sm text-slate-600">Wij verzorgen graag de bezichtigingen bij uw woning. Onze medewerker ontvangt geïnteresseerden, beantwoordt vragen en koppelt alles terug. Plan een afspraak — wij nemen contact op.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={`mailto:info@woonverder.eu?subject=${encodeURIComponent("Bezichtiging via Woon Verder — " + selected.title)}&body=${encodeURIComponent("Ik wil dat Woon Verder de bezichtigingen op mijn woning " + selected.title + " (" + selected.address + ") verzorgt. Neem contact op om een afspraak in te plannen.")}`} className="wv-btn-primary text-sm inline-flex items-center gap-2" data-testid="seller-viewing-request"><Calendar className="w-4 h-4" /> Bezichtiging door Woon Verder</a>
              <Link to="/fotografie" className="wv-btn-outline text-sm inline-flex items-center gap-2"><Camera className="w-4 h-4" /> Extra fotografie boeken</Link>
            </div>
          </div>
        </div>

        <div className="mt-8 flex gap-2 flex-wrap border-b border-slate-200 pb-2 overflow-x-auto">
          {[
            { k: "overview", label: "Overzicht", icon: HomeIcon },
            { k: "matches", label: `Matches${detail.matches ? " (" + detail.matches.length + ")" : ""}`, icon: Sparkles },
            { k: "interests", label: `Interesses${detail.interests ? " (" + detail.interests.length + ")" : ""}`, icon: Heart },
            { k: "viewings", label: `Bezichtigingen${detail.viewings ? " (" + detail.viewings.length + ")" : ""}`, icon: Calendar },
            { k: "offers", label: `Biedingen${detail.offers ? " (" + detail.offers.length + ")" : ""}`, icon: Coins },
            { k: "docs", label: `Documenten (${docs.length})`, icon: FileText },
            { k: "slots", label: `Bezichtiging-slots (${slots.length})`, icon: Clock },
            { k: "notes", label: `Notities (${notes.length})`, icon: StickyNote },
            { k: "settings", label: "Notificaties", icon: notif.enabled ? Bell : BellOff },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap ${tab === t.k ? "bg-[color:var(--wv-navy)] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`sd-tab-${t.k}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {busy && <p className="mt-6 text-slate-500">Laden…</p>}

        {!busy && tab === "overview" && (
          <div className="mt-6 grid md:grid-cols-4 gap-3">
            {[
              ["matches", "Matches", detail.matches?.length || 0, Sparkles],
              ["interests", "Interesses", detail.interests?.length || 0, Heart],
              ["viewings", "Bezichtigingen", detail.viewings?.length || 0, Calendar],
              ["offers", "Biedingen", detail.offers?.length || 0, Coins],
            ].map(([k, label, val, Ic]) => (
              <button key={k} onClick={() => setTab(k)} className="p-5 bg-white border border-slate-200 rounded-2xl text-left hover:border-slate-300" data-testid={`sd-kpi-${k}`}>
                <Ic className="w-5 h-5 text-[color:var(--wv-green)]" />
                <p className="mt-2 text-xs uppercase font-bold tracking-wider text-slate-500">{label}</p>
                <p className="font-mono-num text-3xl font-extrabold mt-1">{val}</p>
              </button>
            ))}
          </div>
        )}

        {!busy && tab === "matches" && (
          <div className="mt-6 space-y-2">
            {detail.matches?.length ? detail.matches.map((m, i) => (
              <div key={i} className="p-4 bg-white border border-slate-200 rounded-xl flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-semibold">{m.buyer.first_name} · <span className="text-slate-500 font-normal">{(m.buyer.locations || []).join(", ") || "—"}</span></p>
                  <p className="text-xs text-slate-500 mt-0.5">Budget max: {m.buyer.budget_max ? eur(m.buyer.budget_max) : "—"} · Termijn: {m.buyer.timeline || "—"} · Alert: {m.buyer.alert_frequency || "—"}</p>
                  <p className="text-xs text-slate-600 mt-1">✓ {(m.matched || []).slice(0, 3).join(" · ")}</p>
                </div>
                <span className="wv-badge-match text-sm">{m.score}%</span>
              </div>
            )) : <p className="text-slate-500">Nog geen matches. Zoekprofielen worden dagelijks toegevoegd.</p>}
            <p className="text-xs text-slate-500 mt-3">Uit privacy-oogpunt zijn volledige contactgegevens van kopers niet zichtbaar. Bij interesse gaat het contact via Woon Verder.</p>
          </div>
        )}

        {!busy && tab === "interests" && (
          <div className="mt-6 space-y-2">
            {detail.interests?.length ? detail.interests.map((it) => (
              <div key={it.id} className="p-4 bg-white border border-slate-200 rounded-xl flex justify-between gap-3">
                <div><p className="font-semibold">{it.name || "Geïnteresseerde koper"}</p><p className="text-xs text-slate-500 font-mono-num">{new Date(it.created_at).toLocaleDateString("nl-NL")}</p>
                {it.message && <p className="text-sm text-slate-600 mt-2 italic">"{it.message}"</p>}</div>
                <p className="wv-status-pill wv-status-review whitespace-nowrap">{INT_STATUS[it.status] || it.status}</p>
              </div>
            )) : <p className="text-slate-500">Nog geen interesses ontvangen.</p>}
          </div>
        )}

        {!busy && tab === "viewings" && (
          <div className="mt-6 space-y-2">
            {detail.viewings?.length ? detail.viewings.map((v) => (
              <div key={v.id} className="p-4 bg-white border border-slate-200 rounded-xl flex justify-between gap-3">
                <div><p className="font-semibold">{v.name}</p><p className="text-xs text-slate-500">Voorkeur: {v.preferred_date} ({v.preferred_time})</p>
                {v.notes && <p className="text-sm text-slate-600 mt-2 italic">"{v.notes}"</p>}</div>
                <p className="wv-status-pill wv-status-review whitespace-nowrap">{VW_STATUS[v.status] || v.status}</p>
              </div>
            )) : <p className="text-slate-500">Nog geen bezichtigingen aangevraagd.</p>}
          </div>
        )}

        {!busy && tab === "offers" && (
          <div className="mt-6 space-y-2">
            {detail.offers?.length ? detail.offers.map((o) => (
              <div key={o.id} className="p-4 bg-white border border-slate-200 rounded-xl flex justify-between gap-3 items-center">
                <div><p className="font-semibold">{o.name}</p>
                  <p className="text-sm mt-1">Bod: <span className="font-mono-num font-bold">{eur(o.amount)}</span> · vraagprijs {eur(o.asking_price)}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Financiering: {o.financing} {o.financing_reservation ? "(met voorbehoud)" : "(zonder voorbehoud)"}</p>
                </div>
                <p className="wv-status-pill wv-status-review whitespace-nowrap">{OF_STATUS[o.status] || o.status}</p>
              </div>
            )) : <p className="text-slate-500">Nog geen biedingen uitgebracht.</p>}
          </div>
        )}

        {!busy && tab === "docs" && (
          <div className="mt-6 space-y-4">
            <div className="p-5 bg-white border border-slate-200 rounded-2xl">
              <p className="font-display font-bold">Document uploaden</p>
              <p className="text-sm text-slate-600 mt-1">Energielabel, eigendomsbewijs, tekeningen. PDF of afbeelding (max 15MB).</p>
              <div className="mt-3 grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</label>
                  <select className="wv-input mt-1" value={uploadKind} onChange={(e) => setUploadKind(e.target.value)} data-testid="doc-kind">
                    {DOC_KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bestand</label>
                  <input type="file" accept="application/pdf,image/*" className="wv-input mt-1" onChange={(e) => uploadDoc(e.target.files?.[0])} data-testid="doc-upload-input" />
                </div>
                <label className="sm:col-span-3 inline-flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={uploadVisible} onChange={(e) => setUploadVisible(e.target.checked)} data-testid="doc-visible-toggle" />
                  Zichtbaar voor kopers op de openbare woningpagina
                </label>
              </div>
            </div>
            <div className="space-y-2">
              {docs.length ? docs.map((d) => (
                <div key={d.id} className="p-4 bg-white border border-slate-200 rounded-xl flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-[color:var(--wv-navy)] mt-0.5" />
                    <div>
                      <p className="font-semibold">{DOC_KINDS.find((k) => k.v === d.kind)?.label || d.kind}</p>
                      <p className="text-xs text-slate-500">{d.original_filename} · {Math.round((d.size||0)/1024)} KB · {d.visible_to_buyers ? "Zichtbaar voor kopers" : "Alleen intern"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="wv-btn-outline text-xs" data-testid={`doc-view-${d.id}`}>Bekijken</a>
                    <button onClick={() => deleteDoc(d.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`doc-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              )) : <p className="text-slate-500">Nog geen documenten geüpload.</p>}
            </div>
          </div>
        )}

        {!busy && tab === "slots" && (
          <div className="mt-6 space-y-4">
            <div className="p-5 bg-white border border-slate-200 rounded-2xl">
              <p className="font-display font-bold">Bezichtiging-slot toevoegen</p>
              <p className="text-sm text-slate-600 mt-1">Kopers kunnen direct online een tijdslot boeken. Bevestiging gaat automatisch per e-mail.</p>
              <div className="mt-3 grid sm:grid-cols-5 gap-3">
                <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Datum</label><input type="date" className="wv-input mt-1" value={slotForm.date} onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })} data-testid="slot-date" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Van</label><input type="time" className="wv-input mt-1" value={slotForm.start_time} onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })} data-testid="slot-start" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tot</label><input type="time" className="wv-input mt-1" value={slotForm.end_time} onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })} data-testid="slot-end" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Max boekingen</label><input type="number" min="1" className="wv-input mt-1" value={slotForm.max_bookings} onChange={(e) => setSlotForm({ ...slotForm, max_bookings: e.target.value })} data-testid="slot-max" /></div>
                <div className="flex items-end"><button onClick={addSlot} className="wv-btn-green w-full" data-testid="slot-add">Slot toevoegen</button></div>
                <div className="sm:col-span-5"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Locatie-opmerking (optioneel)</label><input className="wv-input mt-1" placeholder="Bijv. 'Bel aan bij nr 12'" value={slotForm.location_note} onChange={(e) => setSlotForm({ ...slotForm, location_note: e.target.value })} data-testid="slot-note" /></div>
              </div>
            </div>
            <div className="space-y-2">
              {slots.length ? slots.map((s) => (
                <div key={s.id} className="p-4 bg-white border border-slate-200 rounded-xl flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[color:var(--wv-green-dark)] mt-0.5" />
                    <div>
                      <p className="font-semibold">{new Date(s.date).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "long" })} · {s.start_time}–{s.end_time}</p>
                      <p className="text-xs text-slate-500">{s.bookings_count || 0}/{s.max_bookings} geboekt {s.location_note ? "· " + s.location_note : ""}</p>
                    </div>
                  </div>
                  <button onClick={() => deleteSlot(s.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`slot-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              )) : <p className="text-slate-500">Nog geen slots aangemaakt.</p>}
            </div>
          </div>
        )}

        {!busy && tab === "notes" && (
          <div className="mt-6 space-y-4">
            <div className="p-5 bg-white border border-slate-200 rounded-2xl">
              <p className="font-display font-bold">Nieuwe notitie</p>
              <p className="text-sm text-slate-600 mt-1">Interne notities — alleen voor u zichtbaar. Handig voor herinneringen, gesprekken en observaties.</p>
              <textarea className="wv-input mt-3" rows="3" placeholder="Bijv. 'Buurvrouw gesproken op 12/2 — wil ook verkopen'" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="note-input" />
              <button onClick={addNote} className="mt-3 wv-btn-primary" data-testid="note-add">Notitie toevoegen</button>
            </div>
            <div className="space-y-2">
              {notes.length ? notes.map((n) => (
                <div key={n.id} className="p-4 bg-[color:var(--wv-sand)] border border-slate-200 rounded-xl flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-slate-500 font-mono-num">{new Date(n.created_at).toLocaleString("nl-NL")}</p>
                    <p className="text-slate-800 whitespace-pre-wrap mt-1">{n.text}</p>
                  </div>
                  <button onClick={() => deleteNote(n.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`note-del-${n.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              )) : <p className="text-slate-500">Nog geen notities.</p>}
            </div>
          </div>
        )}

        {!busy && tab === "settings" && (
          <div className="mt-6 max-w-2xl">
            <div className="p-5 bg-white border border-slate-200 rounded-2xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-lg">Match-notificaties per e-mail</p>
                  <p className="text-sm text-slate-600 mt-1">Ontvang direct een e-mail wanneer een nieuw zoekprofiel sterk matcht op deze woning — zodat u niets mist.</p>
                </div>
                <button onClick={() => saveNotif({ ...notif, enabled: !notif.enabled })} className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${notif.enabled ? "bg-[color:var(--wv-green)] text-white" : "bg-slate-200 text-slate-700"}`} data-testid="notif-toggle">
                  {notif.enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />} {notif.enabled ? "Aan" : "Uit"}
                </button>
              </div>
              {notif.enabled && (
                <div className="mt-4">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Minimale match-score voor melding</label>
                  <div className="mt-2 flex items-center gap-3">
                    <input type="range" min="50" max="99" value={notif.min_score} onChange={(e) => setNotif({ ...notif, min_score: parseInt(e.target.value) })} onMouseUp={(e) => saveNotif({ ...notif, min_score: parseInt(e.target.value) })} onTouchEnd={(e) => saveNotif({ ...notif, min_score: parseInt(e.target.value) })} className="flex-1" data-testid="notif-score" />
                    <span className="font-mono-num font-bold w-14 text-right">{notif.min_score}%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">U ontvangt alleen mails wanneer een nieuw profiel op minimaal deze score matcht.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Mijn account</p>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2">Welkom {user?.name?.split(" ")[0]}</h1>
      <p className="mt-2 text-slate-600">Klik op een woning om live matches, interesses, bezichtigingen en biedingen te zien.</p>

      <div className="mt-6 flex justify-between items-center flex-wrap gap-3">
        <p className="font-semibold text-slate-700">Uw woningen ({items.length})</p>
        <Link to="/woning-aanmelden" className="wv-btn-primary inline-flex items-center gap-2" data-testid="seller-add-property"><Plus className="w-4 h-4" /> Nieuwe woning</Link>
      </div>

      <div className="mt-6 grid gap-4">
        {items.length === 0 && (
          <div className="p-8 rounded-2xl border border-dashed border-slate-300 text-center">
            <p className="text-slate-600">Nog geen woningen aangemeld.</p>
            <Link to="/woning-aanmelden" className="mt-4 inline-flex wv-btn-green" data-testid="seller-empty-cta">Woning aanmelden</Link>
          </div>
        )}
        {items.map((p) => (
          <button key={p.id} onClick={() => openProperty(p)} className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 flex flex-wrap items-center gap-4 text-left" data-testid={`seller-prop-${p.id}`}>
            {p.photos?.[0]?.data_url && <img src={p.photos[0].data_url} alt="" className="w-24 h-24 rounded-xl object-cover" />}
            <div className="flex-1 min-w-[200px]">
              <p className="font-display text-lg font-bold">{p.title}</p>
              <p className="text-sm text-slate-500">{p.address}, {p.city}</p>
              <p className="mt-1 font-mono-num text-sm">{eur(p.price)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase font-bold tracking-wider text-slate-500">Status</p>
              <p className="text-sm font-semibold text-[color:var(--wv-navy)]">{STATUS_LABEL[p.status] || p.status}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        ))}
      </div>
    </div>
  );
}
